import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Listing {
  id: string;
  title: string;
  available_date: string;
  price: number;
  neighborhood: string;
}

interface CalendarViewProps {
  listings: Listing[];
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
}

export function CalendarView({ listings, selectedDate, onDateSelect }: CalendarViewProps) {
  const [currentMonth, setCurrentMonth] = React.useState(selectedDate);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getListingsForDate = (date: Date) => {
    return listings.filter(listing => 
      listing.available_date === formatDate(date)
    );
  };

  const days = [];
  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentMonth);

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="h-24" />);
  }

  // Add cells for each day of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dayListings = getListingsForDate(date);
    const isSelected = formatDate(date) === formatDate(selectedDate);
    
    days.push(
      <button
        key={day}
        onClick={() => onDateSelect(date)}
        className={`h-24 border rounded-lg p-2 relative ${
          isSelected 
            ? 'border-primary-400 bg-primary-50' 
            : 'border-gray-200 hover:border-primary-200'
        }`}
      >
        <span className="absolute top-1 right-1 text-sm text-gray-600">{day}</span>
        {dayListings.length > 0 && (
          <div className="mt-4">
            <span className="text-xs font-medium text-primary-600">
              {dayListings.length} listing{dayListings.length !== 1 ? 's' : ''} available
            </span>
          </div>
        )}
      </button>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <h2 className="text-lg font-semibold">
          {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h2>
        
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-4">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-600">
            {day}
          </div>
        ))}
        {days}
      </div>
    </div>
  );
}